import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { buildsApi } from '@/lib/api/builds';
import type { BuildHistory } from '@/types/entities';
import { Rocket, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

interface PublishButtonProps {
  tenantSlug: string;
  onPublished?: () => void;
}

export function PublishButton({ tenantSlug, onPublished }: PublishButtonProps) {
  const [latestBuild, setLatestBuild] = useState<BuildHistory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuilding, setIsBuilding] = useState(false);

  const fetchLatestBuild = useCallback(async () => {
    try {
      const build = await buildsApi.getLatest(tenantSlug);
      setLatestBuild(build);
      console.log(build);
      if (build?.status === 'running' || build?.status === 'pending') {
        setIsBuilding(true);
      } else {
        setIsBuilding(false);
      }
    } catch {
      setLatestBuild(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  const handlePublish = async () => {
    try {
      const newBuild = await buildsApi.triggerBuild(tenantSlug);
      setLatestBuild(newBuild);
      setIsBuilding(true);
    } catch (err) {
      console.error('Failed to trigger build:', err);
    }
  };

  // Poll for build status every 10 seconds when building
  useEffect(() => {
    fetchLatestBuild();

    let interval: ReturnType<typeof setInterval>;
    if (isBuilding) {
      interval = setInterval(fetchLatestBuild, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tenantSlug, isBuilding, fetchLatestBuild]);

  // Notify parent when build completes
  useEffect(() => {
    if (latestBuild?.status === 'success' && !isBuilding) {
      onPublished?.();
    }
  }, [latestBuild?.status, isBuilding, onPublished]);

  const getPublishedAt = () => {
    if (latestBuild?.status === 'success' && latestBuild.completed_at) {
      return new Date(latestBuild.completed_at);
    }
    return null;
  };

  const lastPublishedAt = getPublishedAt();

  return (
    <div className="flex items-center gap-1 flex-row-reverse sm:items-center sm:gap-3">
      <Button
        onClick={handlePublish}
        disabled={isBuilding}
        size="sm"
      >
        {isBuilding ? (
          <>
            <RefreshCw className="size-4 mr-2 animate-spin" />
            Building...
          </>
        ) : (
          <>
              <Rocket className="size-4" />
              <span className="hidden sm:block">Publish</span>
          </>
        )}
      </Button>
      {latestBuild && !isLoading && lastPublishedAt && (
        <span className="text-xs text-muted-foreground sm:text-sm">
          Last published: {format(lastPublishedAt, "dd-MM-yyyy HH:mm")}
        </span>
      )}
    </div>
  );
}
